// Elimination bracket drawing (single + double): one column per round, cards placed from the
// tree (each match centred between the matches that feed it in the same section), elbow
// connectors to the slot the winner goes to. Single: third place under the final.
// Double: upper bracket on top, lower bracket below, grand final (+ reset) to the right.
// Used by the admin season tab and the public /bracket/:id page (readOnly).
import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import { roundLabel, cardStatus, statusLabel, scoresOf, slotSource } from './bracketText';
import './bracket.css';

const W = 236;         // card width
const TOP = 20;        // card header
const ROW = 28;        // team row
const H = TOP + ROW * 2 + 2;
const GAP_X = 64;
const GAP_Y = 18;
const HEAD = 40;       // round headers
const SECTION = 70;    // gap + label between upper and lower bracket
const COL = W + GAP_X;

const sideOf = (m) => m.bracket_side || 'winners';

/** Place one section's rounds: round 1 stacked from y0, later rounds centred on their feeders. */
function placeSection(list, y0, pos) {
  const byRound = {};
  for (const m of list) (byRound[m.round_number] = byRound[m.round_number] || []).push(m);
  const rounds = Object.keys(byRound).map(Number).sort((a, b) => a - b);
  rounds.forEach(r => byRound[r].sort((a, b) => a.position - b.position));
  const ids = new Set(list.map(m => m.id));
  for (const r of rounds) {
    for (const m of byRound[r]) {
      const kids = list.filter(k => ids.has(k.id) && k.winner_to_id === m.id && pos[k.id]);
      const y = kids.length && r !== rounds[0] ? kids.reduce((s, k) => s + pos[k.id].y, 0) / kids.length : y0 + m.position * (H + GAP_Y);
      pos[m.id] = { x: (r - 1) * COL, y };
    }
  }
  const first = byRound[rounds[0]] || [];
  return { byRound, rounds, bottom: y0 + first.length * (H + GAP_Y) };
}

export default function EliminationView({ bracket, matches, teams, readOnly = false, selectedId, onSelect, onSwap, linkPrefix = '/match' }) {
  const { t } = useTranslation();
  const [hoverTeam, setHoverTeam] = useState(null);
  // manual matchups: drag a team onto another team (open matches only, see store.swapSlots)
  const [dragSlot, setDragSlot] = useState(null);   // { match_id, slot }
  const [dropKey, setDropKey] = useState(null);
  const movable = (m, slot) => !!onSwap && !readOnly && !!m[`team${slot}_id`] && m.status !== 'completed' && !m.match_id;
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef(null);
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);

  const layout = useMemo(() => {
    const pos = {};
    const upper = matches.filter(m => sideOf(m) === 'winners' && !m.is_third_place);
    const lower = matches.filter(m => sideOf(m) === 'losers');
    const gfs = matches.filter(m => sideOf(m) === 'grand_final').sort((a, b) => a.round_number - b.round_number);
    const double = lower.length > 0 || gfs.length > 0;
    const LBL = double ? 24 : 0; // room for the "Upper / Lower bracket" label

    const ub = placeSection(upper, LBL + HEAD, pos);
    const ubFinal = (ub.byRound[ub.rounds[ub.rounds.length - 1]] || [])[0];
    let height = ub.bottom;

    const third = matches.find(m => m.is_third_place);
    if (third && ubFinal) { pos[third.id] = { x: pos[ubFinal.id].x, y: pos[ubFinal.id].y + H + 64 }; height = Math.max(height, pos[third.id].y + H + 20); }

    let lb = null, lbTop = 0;
    if (lower.length) {
      lbTop = ub.bottom + SECTION;
      lb = placeSection(lower, lbTop + LBL + HEAD, pos);
      height = lb.bottom;
    }
    const lbFinal = lb ? (lb.byRound[lb.rounds[lb.rounds.length - 1]] || [])[0] : null;

    const mainCols = Math.max(ub.rounds.length, lb ? lb.rounds.length : 0);
    gfs.forEach((g, i) => {
      const yU = ubFinal ? pos[ubFinal.id].y : HEAD;
      const yL = lbFinal ? pos[lbFinal.id].y : yU;
      pos[g.id] = { x: (mainCols + i) * COL, y: (yU + yL) / 2 };
    });

    // champion = the last deciding match
    const finalMatch = gfs.length ? gfs[gfs.length - 1] : (ubFinal || null);
    const cols = mainCols + gfs.length;
    return { pos, ub, lb, lbTop, LBL, gfs, double, third, ubFinal, finalMatch, width: cols * COL + 200, height: height + 10 };
  }, [matches]);

  const fit = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const avail = el.clientWidth - 36;
    setZoom(Math.max(0.45, Math.min(1, avail / layout.width)));
  }, [layout.width]);
  useEffect(() => { fit(); }, [fit]);

  const onDown = (e) => {
    if (e.button !== 0 || e.target.closest('.br-m') || e.target.closest('.br-zoom')) return;
    const el = scrollRef.current;
    drag.current = { x: e.clientX, y: e.clientY, l: el.scrollLeft, t: el.scrollTop };
    setDragging(true);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const el = scrollRef.current;
    el.scrollLeft = drag.current.l - (e.clientX - drag.current.x);
    el.scrollTop = drag.current.t - (e.clientY - drag.current.y);
  };
  const onUp = () => { drag.current = null; setDragging(false); };

  const { pos, ub, lb, lbTop, LBL, gfs, double, third, finalMatch, width, height } = layout;
  const slotY = (slot) => TOP + ROW * (slot - 0.5) + 1;

  const lines = [];
  for (const m of matches) {
    if (!m.winner_to_id || !pos[m.id] || !pos[m.winner_to_id]) continue;
    const a = pos[m.id], b = pos[m.winner_to_id];
    const x1 = a.x + W, y1 = a.y + H / 2;
    const x2 = b.x, y2 = b.y + slotY(m.winner_to_slot);
    const mx = x2 - GAP_X / 2;
    const hl = hoverTeam && m.winner_id === hoverTeam;
    lines.push(<path key={m.id} className={`br-line${m.winner_id ? ' done' : ''}${hl ? ' hl' : ''}`} d={`M${x1},${y1} H${mx} V${y2} H${x2}`} />);
  }

  const champion = finalMatch && finalMatch.status === 'completed' && finalMatch.winner_id ? teams[finalMatch.winner_id] : null;

  const card = (m) => {
    const p = pos[m.id];
    if (!p) return null;
    const st = cardStatus(m);
    const sc = scoresOf(m);
    const clickable = !readOnly || !!m.match_id;
    const row = (slot) => {
      const id = m[`team${slot}_id`];
      const tm = id ? teams[id] : null;
      const won = m.status === 'completed' && m.winner_id && m.winner_id === id;
      const lost = m.status === 'completed' && m.winner_id && id && m.winner_id !== id;
      const can = movable(m, slot);
      const key = `${m.id}:${slot}`;
      const isSrc = dragSlot && dragSlot.match_id === m.id && dragSlot.slot === slot;
      return (
        <div className={`br-t${won ? ' win' : ''}${lost ? ' lose' : ''}${hoverTeam && id === hoverTeam ? ' hl' : ''}${can ? ' movable' : ''}${dropKey === key ? ' drop' : ''}${isSrc ? ' src' : ''}`}
          onMouseEnter={() => id && setHoverTeam(id)} onMouseLeave={() => setHoverTeam(null)}
          draggable={can}
          title={can ? t('bracket.dragHint', 'Drag onto another team to swap them') : undefined}
          onDragStart={can ? (e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); setDragSlot({ match_id: m.id, slot }); } : undefined}
          onDragEnd={() => { setDragSlot(null); setDropKey(null); }}
          onDragOver={can && dragSlot && !isSrc ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropKey !== key) setDropKey(key); } : undefined}
          onDragLeave={() => dropKey === key && setDropKey(null)}
          onDrop={can && dragSlot && !isSrc ? (e) => { e.preventDefault(); e.stopPropagation(); const src = dragSlot; setDragSlot(null); setDropKey(null); onSwap(src, { match_id: m.id, slot }); } : undefined}>
          <span className="seed">{tm && tm.seed ? tm.seed : ''}</span>
          {tm ? <TeamLogo team={tm} size={18} radius={4} /> : null}
          {tm ? <span className="nm" title={tm.name}>{tm.name}</span>
            : <span className="tbd">{st === 'bye' || st === 'walkover' ? t('bracket.bye', 'BYE') : st === 'not_needed' ? '-' : slotSource(t, matches, m, slot)}</span>}
          {sc ? <span className="sc">{sc[slot - 1]}</span> : null}
        </div>
      );
    };
    return (
      <div key={m.id}
        className={`br-m${readOnly ? ' ro' : ''}${readOnly && m.match_id ? ' has-link' : ''}${selectedId === m.id ? ' sel' : ''}${st === 'live' ? ' live' : ''}${st === 'decision' ? ' decision' : ''}${st === 'bye' || st === 'not_needed' ? ' bye' : ''}${sideOf(m) === 'grand_final' ? ' gf' : ''}`}
        style={{ left: p.x, top: p.y, width: W, height: H }}
        onClick={() => {
          if (!clickable) return;
          if (readOnly) { window.location.href = `${linkPrefix}/${m.match_id}`; return; }
          onSelect && onSelect(m);
        }}>
        <div className="br-m-top">
          <span className="code">{m.code}</span>
          <span>BO{m.best_of || 1}</span>
          <span className={`st st-${st}`}>{statusLabel(t, st)}</span>
        </div>
        {row(1)}
        {row(2)}
      </div>
    );
  };

  const heads = (sec, top) => sec.rounds.map(r => {
    const first = (sec.byRound[r] || [])[0];
    return (
      <div key={`${top}:${r}`} className="br-round-head" style={{ left: (r - 1) * COL, top, width: W }}>
        <div className="n">{roundLabel(t, first && first.round_name, r)}</div>
        <div className="b">{first ? `BO${first.best_of || 1}` : ''}</div>
      </div>
    );
  });

  return (
    <div className={`br-canvas-wrap${readOnly ? ' public' : ''}`}>
      <div className="br-zoom">
        <button type="button" onClick={() => setZoom(z => Math.max(0.35, +(z - 0.1).toFixed(2)))} aria-label={t('bracket.zoomOut', 'Zoom out')}>−</button>
        <button type="button" onClick={fit} title={t('bracket.fit', 'Fit to screen')}>{Math.round(zoom * 100)}%</button>
        <button type="button" onClick={() => setZoom(z => Math.min(1.6, +(z + 0.1).toFixed(2)))} aria-label={t('bracket.zoomIn', 'Zoom in')}>+</button>
      </div>
      <div ref={scrollRef} className={`br-canvas${dragging ? ' dragging' : ''}${readOnly ? ' public' : ''}`}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
        <div style={{ width: width * zoom, height: height * zoom }}>
          <div style={{ position: 'relative', width, height, transform: `scale(${zoom})`, transformOrigin: '0 0' }}>
            {double && <div className="br-section ub" style={{ left: 0, top: 0 }}>{t('bracket.upper', 'Upper bracket')}</div>}
            {heads(ub, LBL)}
            {lb && <>
              <div className="br-divider" style={{ left: 0, top: lbTop - SECTION / 2, width: width - 200 }} />
              <div className="br-section lb" style={{ left: 0, top: lbTop }}>{t('bracket.lower', 'Lower bracket')}</div>
              {heads(lb, lbTop + LBL)}
            </>}
            {gfs.map((g, i) => (
              <div key={g.id} className="br-round-head" style={{ left: pos[g.id].x, top: pos[g.id].y - 40, width: W }}>
                <div className="n">{roundLabel(t, g.round_name, g.round_number)}</div>
                <div className="b">BO{g.best_of || 1}</div>
              </div>
            ))}
            <svg className="br-svg" width={width} height={height}>{lines}</svg>
            {matches.map(card)}
            {third && pos[third.id] && (
              <div className="br-third-label" style={{ left: pos[third.id].x, top: pos[third.id].y - 20 }}>
                {roundLabel(t, { key: 'third_place' })} · BO{third.best_of || 1}
              </div>
            )}
            {finalMatch && pos[finalMatch.id] && (
              <div className="br-champ" style={{ left: pos[finalMatch.id].x + W + 28, top: pos[finalMatch.id].y + H / 2 - 24, opacity: champion ? 1 : 0.4 }}>
                <span style={{ fontSize: '1.2rem' }} aria-hidden="true">🏆</span>
                <div>
                  <div className="lbl">{t('bracket.champion', 'Champion')}</div>
                  <div className="nm">{champion ? champion.name : t('bracket.tbd', 'TBD')}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
